import { DownloadOutlined, EyeOutlined, HistoryOutlined } from "@ant-design/icons"
import { App, Button, Divider, Drawer, Empty, Flex, Skeleton, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { downloadFile } from "../../../utils/download-file.ts"
import { getProjectDocumentHistory } from "../../Project/api/project-documents.services.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"

const { Text, Title } = Typography

interface VersionHistoryDrawerProps {
  open: boolean
  fileName: string
  documentId: string
  onClose: () => void
  onPreviewVersion: (version: ProjectDocument) => void
  /** Resolves to whether the restore actually succeeded server-side. */
  onRestore: (version: ProjectDocument) => Promise<boolean>
}

export function VersionHistoryDrawer({
  open,
  fileName,
  documentId,
  onClose,
  onPreviewVersion,
  onRestore
}: VersionHistoryDrawerProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const [versions, setVersions] = useState<ProjectDocument[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setVersions(null)
      setLoadFailed(false)
      return
    }
    let cancelled = false
    setLoadFailed(false)
    void getProjectDocumentHistory(documentId).then((response) => {
      if (cancelled) return
      if (!response.ok) {
        setLoadFailed(true)
        return
      }
      setVersions(response.data ?? [])
    })
    return () => {
      cancelled = true
    }
  }, [open, documentId, retryCount])

  const handleRestore = (version: ProjectDocument) => {
    modal.confirm({
      title: t("files.versionHistory.restoreTitle", { version: version.version }),
      content: t("files.versionHistory.restoreBody", { version: version.version }),
      okText: t("files.versionHistory.restoreConfirm"),
      cancelText: t("files.filters.cancel"),
      onOk: async () => {
        setRestoringId(version.id)
        const succeeded = await onRestore(version)
        setRestoringId(null)
        if (succeeded) onClose()
      }
    })
  }

  return (
    <Drawer
      title={t("files.versionHistory.title")}
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
    >
      <Flex vertical gap={4} className="!mb-4">
        <Title level={5} className="!mb-0 break-all">
          {fileName}
        </Title>
      </Flex>

      {loadFailed ? (
        <Empty description={t("files.versionHistory.loadFailed")}>
          <Button size="small" onClick={() => setRetryCount((count) => count + 1)}>
            {t("files.versionHistory.retry")}
          </Button>
        </Empty>
      ) : versions === null ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : versions.length === 0 ? (
        <Empty description={t("files.versionHistory.empty")} />
      ) : (
        <Flex vertical>
          {versions.map((version, index) => (
            <React.Fragment key={version.id}>
              {index > 0 && <Divider className="!my-3" />}
              <Flex vertical gap={4}>
                <Flex align="center" gap={8}>
                  <Text strong>
                    {t("fileViewer.detailsPanel.versionShort", { version: version.version })}
                  </Text>
                  {version.is_current && (
                    <Tag color="blue" bordered={false}>
                      {t("files.versionHistory.current")}
                    </Tag>
                  )}
                </Flex>
                <Text type="secondary" className="text-xs">
                  {getFormatDate(version.created) ?? version.created}
                  {version.uploaded_by ? ` · ${version.uploaded_by}` : ""}
                  {version.formatted_file_size ? ` · ${version.formatted_file_size}` : ""}
                </Text>
                {version.revision_note && (
                  <Text italic className="text-xs">
                    “{version.revision_note}”
                  </Text>
                )}
                <Flex gap={8}>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    aria-label={t("files.actions.preview")}
                    onClick={() => onPreviewVersion(version)}
                  >
                    {t("files.actions.preview")}
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    aria-label={t("fileViewer.download")}
                    onClick={() => void downloadFile(version.file, fileName)}
                  >
                    {t("fileViewer.download")}
                  </Button>
                  {!version.is_current && (
                    <Button
                      size="small"
                      icon={<HistoryOutlined />}
                      aria-label={t("files.versionHistory.restore")}
                      loading={restoringId === version.id}
                      onClick={() => handleRestore(version)}
                    >
                      {t("files.versionHistory.restore")}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </React.Fragment>
          ))}
        </Flex>
      )}
    </Drawer>
  )
}
